// ./src/components/features/CardPayments/useCardPayments.ts
//
// Can this account take card payments — did Stripe answer that charges are enabled on its connected account? The one
// read behind every "card payments" line: the wizard's Tickets step, the quick ticket sale's screens (#148, one read
// shared through the wizard context), the finish screen, and the event's getting-started steps (#161 b).
//
// ⚠ `status` is the ANSWER of the last check, null when it FAILED — not `useStripeConnect`'s `status`, which keeps the
//   last good answer when a later check fails (a "Check again" that failed would still read "connected").
// ⚠ Only the latest check is kept: an older one answering late must not overwrite a newer one.
// ⚠ Asked AGAIN when the host comes back to this tab or window: "Connect Stripe" opens Stripe in a new tab, and a line —
//   or a Finish button — still saying "not set up" after they connected there would be false (review #148). ⚠ Not once
//   charges are on (#186): nothing is waited for then.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useStripeConnect } from '@/hooks/useStripeConnect';
import type { StripeConnectStatus } from '@/utils';

export interface CardPayments {
  /** The first check has answered (or failed). Before that nothing is claimed either way. */
  checked: boolean;
  /** The last check's answer; null = it failed — unknown, which is neither "set up" nor "not set up". */
  status: StripeConnectStatus | null;
  loading: boolean;
  /** Check again (`refresh`: ask Stripe, not the stored copy). Resolves the answer, null when the check failed. */
  check: (refresh?: boolean) => Promise<StripeConnectStatus | null>;
}

/** `enabled` — check once it is true (a screen that sells nothing never asks). */
export const useCardPayments = (enabled: boolean): CardPayments => {
  const { loadStatus, loading } = useStripeConnect();
  const [checked, setChecked] = useState(false);
  const [status, setStatus] = useState<StripeConnectStatus | null>(null);
  const callRef = useRef(0);

  const check = useCallback(
    async (refresh?: boolean): Promise<StripeConnectStatus | null> => {
      const call = ++callRef.current;
      const answer = await loadStatus(refresh);

      if (call === callRef.current) {
        setStatus(answer);
        setChecked(true);
      }

      return answer;
    },
    [loadStatus],
  );

  useEffect(() => {
    if (enabled && !checked) {
      void check();
    }
  }, [enabled, checked, check]);

  // ⚠ #186 — only while the answer can still change for the better: once Stripe said charges are on there is nothing to
  //   come back for, and every focus re-asked Stripe itself (`refresh`) for the same answer (#184's review).
  // ⓘ The cost, accepted: a STORED "on" that has gone stale (a missed webhook; Stripe restricted the account) is not
  //   re-asked while the screen stays open — the line reads "ready" until a new visit. The server refuses the charge
  //   either way, so it is a wrong line, never a wrong sale.
  const settledOn = status?.chargesEnabled === true;

  useEffect(() => {
    if (!enabled || settledOn) {
      return;
    }

    const again = () => {
      if (document.visibilityState === 'visible') {
        void check(true);
      }
    };

    window.addEventListener('focus', again);
    document.addEventListener('visibilitychange', again);

    return () => {
      window.removeEventListener('focus', again);
      document.removeEventListener('visibilitychange', again);
    };
  }, [enabled, settledOn, check]);

  return useMemo(() => ({ checked, status, loading, check }), [checked, status, loading, check]);
};
